import { Injectable } from "@angular/core";
import { IAutoplayOptions } from "../../core/round/store/interfaces";
import { RemoveUserBetUseCase } from "../../core/round/domain/remove-user-bet.use-case";
import { GetAutoplayUseCase } from "../../core/round/domain/get-autoplay-options.use-case";
import { AutoPlayRepository } from "../auto-play.repository";
import { catchError, distinctUntilChanged, NEVER, Observable, of, pairwise, Subject, switchMap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BetPanelsService } from "../bet-panels.service";
import { EAutoplayStopReason, IAutoplayStopData } from "../interfaces";

@Injectable()
export class GetAutoplaySendBetUseCase {
    private prevGameIdleWallet: number = 0;
    private autoplayStopReason$: Subject<IAutoplayStopData> = new Subject<IAutoplayStopData>();

    constructor(
        private getAutoplayUseCase: GetAutoplayUseCase,
        private removeUserBetUseCase: RemoveUserBetUseCase,
        private betPanelsService: BetPanelsService,
        private autoPlayRepository: AutoPlayRepository
    ) {

        [0, 1].forEach(betIndex => {
            this.getAutoplayUseCase.execute(betIndex)
                .pipe(
                    distinctUntilChanged((previous, current) => {
                        return previous?.roundCount === current?.roundCount;
                    }),
                    pairwise(),
                    switchMap((result: [IAutoplayOptions | undefined, IAutoplayOptions]) => {
                        const prevResult: IAutoplayOptions | undefined = result[0];
                        const currResult: IAutoplayOptions | undefined = result[1];

                        if (prevResult === undefined && !!currResult && currResult.roundCount > 0) {
                            return of(result[1].betArgs);
                        }
                        // stop
                        if (!!prevResult && (!currResult || currResult.roundCount <= 0)) {
                            if (!!currResult && currResult.roundCount <= 0) {
                                this.autoplayStopReason$.next({
                                    reason: EAutoplayStopReason.RoundCount,
                                    data: [currResult.initialRoundCount]
                                });
                            }
                            this.prevGameIdleWallet = 0;
                            return NEVER;
                        }
                        return this.autoPlayRepository.getUserWallet().pipe(
                            switchMap(wallet => {
                                if (currResult.maxAccumulatedAmount &&
                                    wallet - (this.prevGameIdleWallet || wallet) >= currResult.maxAccumulatedAmount) {
                                    this.autoplayStopReason$.next({
                                        reason: EAutoplayStopReason.SingleWinAmountExceed,
                                        data: [currResult.maxAccumulatedAmount, wallet - (this.prevGameIdleWallet || wallet)]
                                    });
                                    return this.clearAutoBet(betIndex);
                                } else if (wallet && currResult.initialWalletAmount && currResult.maxStackIncreaseAmount &&
                                    wallet >= (currResult.initialWalletAmount + currResult.maxStackIncreaseAmount)) {
                                    this.autoplayStopReason$.next({
                                        reason: EAutoplayStopReason.CashIncrease,
                                        data: [wallet - currResult.initialWalletAmount]
                                    });
                                    return this.clearAutoBet(betIndex);
                                } else if (wallet && currResult.initialWalletAmount && currResult.maxStackDecreaseAmount &&
                                    wallet <= (currResult.initialWalletAmount - currResult.maxStackDecreaseAmount)) {
                                    this.autoplayStopReason$.next({
                                        reason: EAutoplayStopReason.CashDecrease,
                                        data: [currResult.initialWalletAmount - wallet]
                                    });
                                    return this.clearAutoBet(betIndex);
                                }
                                this.prevGameIdleWallet = wallet;
                                return of(currResult.betArgs);
                            }),
                            catchError(e => {
                                console.error('Get wallet error:', e);
                                return this.clearAutoBet(betIndex);
                            })
                        )
                    }),
                    takeUntilDestroyed()
                )
                .subscribe(args => {
                    this.betPanelsService.sendBet(args).catch(e => {
                        console.error('sendBet error', e);
                    })
                });
        })
    }

    public subscribeToAutoplayStopReason(): Observable<IAutoplayStopData> {
        return this.autoplayStopReason$.asObservable();
    }

    private clearAutoBet(betIndex: number): Observable<never> {
        this.removeUserBetUseCase.execute(betIndex);
        this.prevGameIdleWallet = 0;
        return NEVER;
    }
}
